import axios from "axios";
import { getApiConfig } from "@/lib/api/config";
import {
  ApiError,
  extractEnvelopeMessage,
  formatUserErrorMessage,
} from "@/lib/api/errors";
import { refreshAppTokens } from "@/lib/auth/token-session";

const { baseUrl } = getApiConfig();

export const axiosPrivate = axios.create({
  baseURL: baseUrl || undefined,
  headers: {
    "Content-Type": "application/json",
    "ngrok-skip-browser-warning": "true",
  },
});

let handlingUnauthorized = false;

type RetryableAxiosConfig = {
  _retry?: boolean;
  url?: string;
  headers?: Record<string, string>;
};

function isLogoutRequest(url?: string): boolean {
  if (!url) return false;
  return /\/logout(?:\?|$|\/)/i.test(url);
}

function isRefreshRequest(url?: string): boolean {
  if (!url) return false;
  return /\/token\/refresh(?:\?|$|\/)/i.test(url);
}

function logoutOnUnauthorized(): void {
  if (typeof window === "undefined" || handlingUnauthorized) return;
  handlingUnauthorized = true;
  void import("@/lib/auth").then(({ signOutAndReload }) => {
    void signOutAndReload();
  });
}

axiosPrivate.interceptors.request.use(async (config) => {
  const { getValidAccessToken } = await import("@/lib/auth/token-session");
  const token = (await getValidAccessToken())?.trim();

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

axiosPrivate.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!axios.isAxiosError(error)) {
      return Promise.reject(error);
    }

    const status = error.response?.status ?? 0;
    const originalRequest = error.config as RetryableAxiosConfig | undefined;

    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshRequest(originalRequest.url) &&
      !isLogoutRequest(originalRequest.url)
    ) {
      originalRequest._retry = true;
      const refreshed = await refreshAppTokens();
      if (refreshed?.accessToken) {
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${refreshed.accessToken}`;
        return axiosPrivate(originalRequest);
      }
      logoutOnUnauthorized();
    } else if (status === 401 && !isLogoutRequest(originalRequest?.url)) {
      logoutOnUnauthorized();
    }

    const raw =
      extractEnvelopeMessage(error.response?.data) ?? error.message;
    const message = formatUserErrorMessage(raw, status, "generic");

    return Promise.reject(new ApiError(message, status));
  },
);
